import { Toast } from '@base-ui/react/toast';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import CustomAvatar from '@/components/common/CustomAvatar';
import { NavChevron } from '@/components/Icons';
import { Body, Meta, NotificationFallbackGlyph, Preview, TitleText } from '@/components/Notifications/Notification';
import CrossIcon from '@/images/cross.svg?react';

/**
 * Two toast flavours on one Base UI primitive:
 *
 * - **Snackbar** — bottom-center transient bar for the result of something the user just did.
 * - **Notification toast** — top-right card for an incoming push (avatar, heading, click-through).
 *
 * Each gets its own manager + provider so the two stacks queue and expand independently.
 * Base UI handles the auto-dismiss timer, pause-on-hover, stack limit and swipe-to-dismiss,
 * which is what the hand-rolled `ToastManager` used to do.
 */

type SnackbarAction = { label: string; to: string };
type SnackbarData = { action?: SnackbarAction };

export type NotificationToastOptions = {
  heading: string;
  /** Body preview. Omit to render heading + helper text only. */
  content?: string;
  /** e.g. "Live Trade" / "Demo Trade". */
  helperText?: string;
  /** Explicit avatar URL (chat). Falls back to `cognitoIds` avatars. */
  icon?: string;
  cognitoIds?: string[];
  onClick?: () => void;
};

const snackbarManager = Toast.createToastManager<SnackbarData>();
const notificationManager = Toast.createToastManager<NotificationToastOptions>();

/** Renders `message` as-is — punctuation belongs to the caller. */
export const showSnackbar = (message: string, action?: SnackbarAction) => snackbarManager.add({ title: message, data: { action } });

export const showNotificationToast = (options: NotificationToastOptions) =>
  notificationManager.add({ title: options.heading, description: options.content, data: options });

// ── Shared styling ──

const surface = css`
  /* No global border-box reset in this app — without this the padding overflows the viewport. */
  box-sizing: border-box;
  background: ${props => props.theme.brand.gradCard};
  color: ${props => props.theme.brand.textPrimary};
  border: 1px solid ${props => props.theme.brand.strokeSoft};
  box-shadow: ${props => props.theme.brand.shadowCard};

  &:focus-visible {
    outline: 2px solid ${props => props.theme.brand.royalCobalt};
    outline-offset: 2px;
  }
`;

const viewport = css`
  position: fixed;
  z-index: ${props => props.theme.zIndex.toast};
  /* Toasts are absolutely positioned inside, so the viewport box itself must not eat clicks. */
  pointer-events: none;
  outline: none;
`;

/**
 * Base UI's stacking recipe (as shipped by shadcn). Collapsed, every toast is pinned to the
 * anchored edge at the frontmost toast's height, peeking out behind it and scaled down; expanded
 * (hover/focus) they fan out to their own heights using `--toast-offset-y`.
 *
 * `anchor` is the edge the stack grows from: `bottom` for the snackbar, `top` for notifications.
 * `--dir` carries the sign so the two variants share one set of calcs.
 *
 * `edgeInset` is how far the viewport sits from that screen edge. Enter/exit has to travel the
 * toast's own height *plus* that inset to actually leave the screen — a flat 150% is enough for a
 * viewport parked 16px off the edge, but the notification viewport is 94px down, so its toasts
 * were unmounting while still visible.
 */
const stacked = (anchor: 'top' | 'bottom', edgeInset: string) => css`
  position: absolute;
  ${anchor}: 0;
  left: 0;
  right: 0;
  width: 100%;
  transform-origin: ${anchor};
  will-change: transform;
  user-select: none;
  pointer-events: auto;
  touch-action: none;
  /* Frontmost paints on top of the toasts stacked behind it. */
  z-index: calc(1000 - var(--toast-index));

  --dir: ${anchor === 'top' ? 1 : -1};
  --gap: 12px;
  --peek: 12px;
  --height: var(--toast-frontmost-height, var(--toast-height));
  --scale: calc(max(0, 1 - var(--toast-index) * 0.1));
  --shrink: calc(1 - var(--scale));
  --offset-y: calc((var(--toast-offset-y) + var(--toast-index) * var(--gap)) * var(--dir) + var(--toast-swipe-movement-y));
  /* Clear of the screen edge: the toast's own height plus the viewport's inset from that edge. */
  --off-screen: calc((100% + ${edgeInset}) * var(--dir) * -1);

  height: var(--height);
  transform: translateX(var(--toast-swipe-movement-x))
    translateY(
      calc(var(--toast-swipe-movement-y) + (var(--toast-index) * var(--peek) + var(--shrink) * var(--height)) * var(--dir))
    )
    scale(var(--scale));
  transition:
    transform 500ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 500ms,
    height 150ms;

  /* Bridges the gap between stacked toasts so the hover that expands them doesn't flicker. */
  &::after {
    content: '';
    position: absolute;
    ${anchor === 'top' ? 'bottom' : 'top'}: 100%;
    left: 0;
    width: 100%;
    height: calc(var(--gap) + 1px);
  }

  &[data-expanded] {
    height: var(--toast-height);
    transform: translateX(var(--toast-swipe-movement-x)) translateY(var(--offset-y));
  }

  /* Over the limit: still mounted so it can animate out, but invisible — so it must not keep
     swallowing clicks meant for the page underneath. */
  &[data-limited] {
    opacity: 0;
    pointer-events: none;
  }

  &[data-starting-style] {
    transform: translateY(var(--off-screen));
  }

  /* An expanded toast low in the stack is further from the edge than --off-screen covers, so it
     fades as it goes rather than blinking out mid-screen. */
  &[data-ending-style] {
    opacity: 0;
  }

  /* Dismissal flies out the way it was swiped, or back past the anchored edge when timed out. */
  &[data-ending-style]:not([data-limited]):not([data-swipe-direction]) {
    transform: translateY(var(--off-screen));
  }

  &[data-ending-style][data-swipe-direction='up'] {
    transform: translateY(calc(var(--toast-swipe-movement-y) - 150%));
  }

  &[data-ending-style][data-swipe-direction='down'] {
    transform: translateY(calc(var(--toast-swipe-movement-y) + 150%));
  }

  &[data-ending-style][data-swipe-direction='left'] {
    transform: translateX(calc(var(--toast-swipe-movement-x) - 150%)) translateY(var(--offset-y));
  }

  &[data-ending-style][data-swipe-direction='right'] {
    transform: translateX(calc(var(--toast-swipe-movement-x) + 150%)) translateY(var(--offset-y));
  }
`;

/**
 * The toast's own box: the root is height-locked to the frontmost toast, so padding lives here.
 *
 * `flex-start`, not centred: while collapsed every toast is forced to the frontmost's height, and
 * centring spends that difference as padding above *and* below — a short toast behind a tall one
 * looks over-padded, a tall one gets clipped at both ends. Top-aligned, the surplus (or the clip)
 * is all at the bottom and the visible top edge of every toast is identical.
 */
const stackedContent = css`
  display: flex;
  align-items: flex-start;
  /* Must be border-box: height 100% resolves against the root's CONTENT box, so a content-box
     content would then add its own padding on top and overflow the height-locked root — which
     clipped every toast, frontmost included, by exactly the vertical padding. */
  box-sizing: border-box;
  height: 100%;
  overflow: hidden;
  transition: opacity 250ms cubic-bezier(0.22, 1, 0.36, 1);

  /* Only the frontmost toast shows its contents while the stack is collapsed. */
  &[data-behind] {
    opacity: 0;
  }

  &[data-expanded] {
    opacity: 1;
  }
`;

// ── Snackbar ──

/** Distance from the screen edge — shared by the viewport offset and the enter/exit travel. */
const SNACKBAR_EDGE = '24px';

const SnackbarViewport = styled(Toast.Viewport)`
  ${viewport};
  bottom: ${SNACKBAR_EDGE};
  left: 0;
  right: 0;
  margin: 0 auto;
  width: min(440px, calc(100vw - 32px));

  @media (${props => props.theme.device.mobile}) {
    bottom: 16px;
  }
`;

const SnackbarRoot = styled(Toast.Root)`
  ${surface};
  ${stacked('bottom', SNACKBAR_EDGE)};
  border-radius: ${props => props.theme.brand.radii.sm};
  /* A bar sized to its message, as before. left/right 0 plus auto margins centre it without
     spending the transform the stack needs. */
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
`;

const SnackbarContent = styled(Toast.Content)`
  ${stackedContent};
  /* Single-line bar: centred reads better than top-aligned, and matches the old snackbar. */
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  font-size: 14px;
  line-height: 20px;
`;

/* Toast.Title renders an <h2>, so the UA's 1.5em/bold has to be reset back to the bar's own type. */
const SnackbarMessage = styled(Toast.Title)`
  min-width: 0;
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  font-weight: 400;
`;

const SnackbarLinkText = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  color: inherit;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;

  :hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/** Down chevron rotated to point at the destination — replaces the old literal `>` in the label. */
const LinkChevron = styled(NavChevron)`
  flex-shrink: 0;
  transform: rotate(-90deg);
`;

function SnackbarList() {
  const { toasts } = Toast.useToastManager<SnackbarData>();

  return toasts.map(toast => (
    <SnackbarRoot key={toast.id} toast={toast} swipeDirection='down'>
      <SnackbarContent>
        <SnackbarMessage>{toast.title}</SnackbarMessage>
        {toast.data?.action && (
          <SnackbarLinkText to={toast.data.action.to}>
            {toast.data.action.label}
            <LinkChevron width={16} height={16} />
          </SnackbarLinkText>
        )}
      </SnackbarContent>
    </SnackbarRoot>
  ));
}

// ── Notification toast ──

const NOTIFICATION_EDGE = '94px';

const NotificationViewport = styled(Toast.Viewport)`
  ${viewport};
  top: ${NOTIFICATION_EDGE};
  right: 12px;
  width: min(448px, calc(100vw - 24px));

  @media (${props => props.theme.device.laptop}) {
    top: 88px;
  }
`;

const NotificationRoot = styled(Toast.Root)`
  ${surface};
  ${stacked('top', NOTIFICATION_EDGE)};
  border-radius: ${props => props.theme.brand.radii.md};
  cursor: pointer;
`;

/* Gap and padding match the notification list row in `Notifications/Notification.tsx`. */
const NotificationContent = styled(Toast.Content)`
  ${stackedContent};
  gap: 14px;
  padding: 16px;
  text-align: left;
`;

const NotificationIcon = styled.img`
  width: 45px;
  height: 45px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
`;

const NotificationAvatar = styled.span`
  display: flex;
  flex-shrink: 0;
  pointer-events: none;
`;

const CloseButton = styled(Toast.Close)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: ${props => props.theme.brand.textSecondary};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  :hover {
    background-color: ${props => props.theme.brand.surface2};
  }
`;

function NotificationList() {
  const { t } = useTranslation();
  const { toasts } = Toast.useToastManager<NotificationToastOptions>();

  return toasts.map(toast => {
    const { icon, cognitoIds, content, helperText, onClick } = toast.data ?? {};
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || !onClick || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      onClick();
    };

    return (
      <NotificationRoot key={toast.id} toast={toast} swipeDirection={['up', 'right']} onClick={onClick} onKeyDown={handleKeyDown}>
        <NotificationContent>
          <NotificationAvatar>
            {icon ? (
              <NotificationIcon alt='' src={icon} />
            ) : (
              <CustomAvatar cognitoIds={cognitoIds} PlaceholderImage={<NotificationFallbackGlyph />} />
            )}
          </NotificationAvatar>
          <Body>
            <Toast.Title render={<TitleText $isUnread />}>{toast.title}</Toast.Title>
            {content && <Toast.Description render={<Preview />}>{content}</Toast.Description>}
            {helperText && <Meta>{helperText}</Meta>}
          </Body>
          {/* Stop the card's click-through from firing when the user only meant to dismiss. */}
          <CloseButton aria-label={t('Close')} onClick={event => event.stopPropagation()}>
            <CrossIcon aria-hidden='true' />
          </CloseButton>
        </NotificationContent>
      </NotificationRoot>
    );
  });
}

/** Mount once per layout that needs toasts. */
function Toaster() {
  const { t } = useTranslation();
  return (
    <>
      <Toast.Provider toastManager={snackbarManager} limit={3}>
        <Toast.Portal>
          <SnackbarViewport aria-label={t('Notifications')}>
            <SnackbarList />
          </SnackbarViewport>
        </Toast.Portal>
      </Toast.Provider>
      <Toast.Provider toastManager={notificationManager} limit={3}>
        <Toast.Portal>
          <NotificationViewport aria-label={t('Notifications')}>
            <NotificationList />
          </NotificationViewport>
        </Toast.Portal>
      </Toast.Provider>
    </>
  );
}

export default Toaster;
